import React, { useState, useEffect } from 'react';
import {
  Building, Hammer, Star, Calendar, MapPin,
  ArrowRight, Eye, Award, Search,
  TrendingUp, Users, Clock, Home, Grid3X3, Trash2, CheckCircle, X, Pencil, Anvil,
  ChevronLeft, ChevronRight, ZoomIn, RotateCcw
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import { colors, componentStyles } from '../components/DesignSystem.jsx';
import AnimatedCounter from '../components/AnimatedCounter';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Gallery carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);



  useEffect(() => {
    document.title = "Portfolio - Ceballos Total Framing | Arizona's Premier Structural Projects";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore our portfolio of exceptional wood and steel framing projects. From residential to commercial, see how we deliver master-level craftsmanship across Arizona.');
    }
  }, []);

  const categories = [
    { id: 'all', name: 'All Projects', icon: Grid3X3 },
    { id: 'wood', name: 'Wood Framing', icon: Hammer },
    { id: 'steel', name: 'Steel Structural', icon: Anvil },
    { id: 'both', name: 'Dual Mastery', icon: Award },
    { id: 'demo', name: 'Demolition', icon: Trash2 },
    { id: 'commercial', name: 'Commercial', icon: Building },
    { id: 'residential', name: 'Residential', icon: Home }
  ];

  const projects = [
    {
      id: 1,
      title: 'Paradise Valley Luxury Custom Home',
      category: 'both',
      type: 'residential',
      location: 'Paradise Valley, AZ',
      duration: '10 weeks',

      description: 'Dual mastery project combining premium wood framing with advanced steel structural elements for a luxury custom home.',
      features: ['Premium timber framing', 'Steel structural beams', 'Exposed architectural elements', 'Complex roof geometry', 'Custom connections'],
      image: '/ParadiseValley_McIntyreDevelopmentInterior.jpg',
      images: [
        '/ParadiseValley_McIntyreDevelopmentInterior.jpg',
        '/ParadiseValley_McIntyreDevelopment_StructuralSteel.jpg',
        '/ParadiseValley_McIntyreDevelopmentPatio.jpg',
        '/ParadiseValley_McIntyreDevelopmentInterior2.jpg',
        '/ParadiseValley_McIntyreDevelopmentSteelHighView.jpg'
      ],
      rating: 5,
      completed: 'January 2025',
      builder: 'Dan McIntyre, McIntyre Development'
    },
    {
      id: 2,
      title: 'Cave Creek Luxury Custom Home',
      category: 'both',
      type: 'residential',
      location: 'Cave Creek, AZ',
      duration: '12 weeks',

      description: 'Sophisticated dual mastery project featuring premium wood framing with advanced steel structural elements, designed by Spry Architecture.',
      features: ['Premium timber framing', 'Steel structural beams', 'Custom architectural details', 'Complex roof geometry', 'Exposed structural elements'],
      image: '/CaveCreek_SpryArchitecture_SymmetryBuildersWoodFraming.jpg',
      images: [
        '/CaveCreek_SpryArchitecture_SymmetryBuildersWoodFraming.jpg',
        '/CaveCreek_SpryArchitecture_SymmetryBuildersSteelandWood.jpg',
        '/CaveCreek_SpryArchitecture_SymmetryBuildersSteelandWood2.jpg',
        '/CaveCreek_SpryArchitecture_SymmetryBuildersLongSteelBeam.jpg'
      ],
      rating: 5,
      completed: '2023',
      builder: 'Symmetry Builders',
      architect: 'Spry Architecture'
    },
    {
      id: 3,
      title: 'O\'Neil Residence Demolition',
      category: 'demo',
      type: 'residential',
      location: 'Paradise Valley, AZ',
      duration: '3 weeks',

      description: 'Precision demolition of luxury residence with selective structural removal and site preparation for new construction.',
      features: ['Selective structural removal', 'Site protection protocols', 'Debris management', 'Environmental compliance', 'Precision demolition'],
      image: '/ParadiseValley_O\'NeilResidenceDemolition1.jpg',
      images: [
        '/ParadiseValley_O\'NeilResidenceDemolition1.jpg',
        '/ParadiseValley_O\'NeilResidenceDemolition2.jpg',
        '/ParadiseValley_O\'NeilResidenceDemolition3.jpg',
        '/ParadiseValley_O\'NeilResidenceDemolition4.jpg',
        '/ParadiseValley_O\'NeilResidenceDemolition5.jpg',
        '/ParadiseValley_O\'NeilResidenceDemolition6.jpg'
      ],
      rating: 5,
      completed: 'June 2023'
    },
    {
      id: 4,
      title: 'Puri Residence',
      category: 'both',
      type: 'residential',
      location: 'Paradise Valley, AZ',
      duration: '14 weeks',

      description: 'Luxury residential project featuring massive steel beams with premium wood framing, showcasing structural engineering excellence.',
      features: ['Huge steel beams', 'Premium wood framing', 'Structural engineering', 'Large spans', 'Custom connections', 'Dual mastery expertise'],
      image: '/PuriResidenceSymmetryBuilders1.jpg',
      images: [
        '/PuriResidenceSymmetryBuilders1.jpg',
        '/PuriResidenceSymmetryBuilders2.jpg',
        '/PuriResidenceSymmetryBuilders3.jpg',
        '/PuriResidenceSymmetryBuilders4.jpg',
        '/PuriResidenceSymmetryBuilders5.jpg'
      ],
      rating: 5,
      completed: 'May 2023',
      builder: 'Symmetry Builders'
    },
    {
      id: 5,
      title: 'Scottsdale Legacy Remodeling Inc',
      category: 'wood',
      type: 'residential',
      location: 'Scottsdale, AZ',
      duration: '8 weeks',

      description: 'Premium wood siding installation and wood ceiling renovation for a luxury Scottsdale residence, showcasing traditional craftsmanship and attention to detail.',
      features: ['Premium wood siding materials', 'Wood ceiling installation', 'Custom trim work', 'Weather protection systems', 'Architectural integration'],
      image: '/Scottsdale_LegacyRemodelingInc.JPEG',
      images: [
        '/Scottsdale_LegacyRemodelingInc.JPEG',
        '/Scottsdale_LegacyRemodelingInc2.JPEG',
        '/Scottsdale_LegacyRemodelingInc3.JPEG'
      ],
      rating: 5,
      completed: '2024',
      builder: 'Legacy Remodeling Inc'
    },
    {
      id: 6,
      title: 'Fountain Hills Interior Wood Work',
      category: 'wood',
      type: 'residential',
      location: 'Fountain Hills, AZ',
      duration: '6 weeks',

      description: 'All wood interior work including custom window installations and interior wood framing for a Fountain Hills residence.',
      features: ['Interior wood framing', 'Custom window installations', 'Wood interior work', 'Precision craftsmanship', 'Interior renovations', 'Custom wood details'],
      image: '/FountainHills_KimberlyBucker1.jpg',
      images: [
        '/FountainHills_KimberlyBucker1.jpg',
        '/FountainHills_KimberlyBucker2.jpg',
        '/FountainHills_KimberlyBucker3.jpg',
        '/FountainHills_KimberlyBucker4.jpg',
        '/FountainHills_KimberlyBucker5.jpg'
      ],
      rating: 5,
      completed: 'October 2021',
      builder: 'Kimberly Bucker'
    },
    {
      id: 7,
      title: 'Wickenberg Steel Structural',
      category: 'steel',
      type: 'residential',
      location: 'Wickenberg, AZ',
      duration: '6 weeks',

      description: 'Advanced steel structural framing project featuring custom welded connections and architectural steel elements.',
      features: ['Steel structural framing', 'Custom welded connections', 'Architectural steel design', 'Precision engineering', 'Modern aesthetics'],
      image: '/Wickenburg_SpryArchitecture8.jpg',
      images: [
        '/Wickenburg_SpryArchitecture8.jpg',
        '/Wickenburg_SpryArchitecture1.jpg',
        '/Wickenburg_SpryArchitecture2.jpg',
        '/Wickenburg_SpryArchitecture3.jpg',
        '/Wickenburg_SpryArchitecture4.jpg',
        '/Wickenburg_SpryArchitecture5.jpg',
        '/Wickenburg_SpryArchitecture6.jpg',
        '/Wickenburg_SpryArchitecture7.jpg'
      ],
      rating: 5,
      completed: 'October 2022',
      architect: 'Spry Architecture'
    },
    {
      id: 8,
      title: 'Arrollo Verde Ramada',
      category: 'wood',
      type: 'residential',
      location: 'Phoenix, AZ',
      duration: '4 weeks',

      description: 'Custom outdoor ramada construction featuring premium wood framing and architectural integration with Camelback Mountain views.',
      features: ['Premium wood framing', 'Outdoor construction', 'Architectural integration', 'Mountain view design', 'Custom detailing'],
      image: '/ArolloVerde_CamelbackMountain_Ramada.jpg',
      images: [
        '/ArolloVerde_CamelbackMountain_Ramada.jpg'
      ],
      rating: 5,
      completed: 'August 2022'
    }
  ];

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => 
        project.category === selectedCategory || project.type === selectedCategory
      ));
    }
  }, [selectedCategory]);

  // Gallery images - curated selection from projects
  const galleryImages = [
    { src: '/ParadiseValley_McIntyreDevelopmentInterior.jpg', title: 'Paradise Valley Luxury Interior', category: 'Wood Framing' },
    { src: '/ParadiseValley_McIntyreDevelopment_StructuralSteel.jpg', title: 'Structural Steel Installation', category: 'Steel Framing' },
    { src: '/CaveCreek_SpryArchitecture_SymmetryBuildersWoodFraming.jpg', title: 'Cave Creek Timber Work', category: 'Wood Framing' },
    { src: '/CaveCreek_SpryArchitecture_SymmetryBuildersSteelandWood.jpg', title: 'Steel and Wood Integration', category: 'Dual Mastery' },
    { src: '/Peoria_MalapaiBuilders1.jpg', title: 'Peoria Project', category: 'Commercial' },
    { src: '/ParadiseValley_O\'NeilResidenceDemolition1.jpg', title: 'Precision Demolition', category: 'Demolition' },
    { src: '/ParadiseValley_McIntyreDevelopmentPatio.jpg', title: 'Outdoor Framing Excellence', category: 'Wood Framing' },
    { src: '/CaveCreek_SpryArchitecture_SymmetryBuildersLongSteelBeam.jpg', title: 'Long Steel Beam Installation', category: 'Steel Framing' },
    { src: '/ParadiseValley_McIntyreDevelopmentInterior2.jpg', title: 'Luxury Interior Detail', category: 'Wood Framing' },
    { src: '/ParadiseValley_McIntyreDevelopmentSteelHighView.jpg', title: 'Steel Framework Overview', category: 'Steel Framing' },
    { src: '/Prugh_1.jpg', title: 'Cave Creek Modern Steel House', category: 'Steel Framing' },
    { src: '/Prugh_2.jpg', title: 'Modern Steel Structural Design', category: 'Steel Framing' },
    { src: '/Prugh_3.jpg', title: 'Contemporary Steel Architecture', category: 'Steel Framing' }
  ];

  // Initialize filtered projects on component mount
  useEffect(() => {
    setFilteredProjects(projects);
  }, []);

  // Auto-rotation effect for gallery carousel
  useEffect(() => {
    if (!isAutoPlaying || isFullscreenOpen) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    }, 5000); // 5 seconds - industry standard

    return () => clearInterval(interval);
  }, [isAutoPlaying, isFullscreenOpen, galleryImages.length]);

  const portfolioStats = [
    { label: 'Projects Completed', value: 25, suffix: '+', icon: Building },
    { label: 'Years of Experience', value: 25, suffix: '+', icon: Star },
    { label: 'Client Satisfaction', value: 100, suffix: '%', icon: Award },
    { label: 'Project Value', value: 12, suffix: 'M+', prefix: '$', icon: TrendingUp }
  ];

  // Gallery Carousel Component
  const GalleryCarousel = () => {
    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };

    const openFullscreen = (index) => {
      setFullscreenImageIndex(index);
      setIsFullscreenOpen(true);
    };

    const toggleAutoPlay = () => {
      setIsAutoPlaying(!isAutoPlaying);
    };

    const nextFullscreenImage = () => {
      setFullscreenImageIndex((prev) => (prev + 1) % galleryImages.length);
    };

    const prevFullscreenImage = () => {
      setFullscreenImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };

    return (
      <>
        {/* Carousel Container */}
        <div className="relative max-w-6xl mx-auto">
          {/* Main Image Display */}
          <div className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden border border-gray-700 shadow-2xl" style={{ isolation: 'isolate' }}>
            <img
              src={galleryImages[currentImageIndex].src}
              alt={galleryImages[currentImageIndex].title}
              className="w-full h-full object-cover transition-all duration-700"
            />
            
            {/* Image Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            
            {/* Image Info */}
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-2xl font-serif text-white mb-2">
                {galleryImages[currentImageIndex].title}
              </h3>
              <p className="text-amber-400 font-light tracking-wide uppercase text-sm">
                {galleryImages[currentImageIndex].category}
              </p>
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-400 transition-all duration-300 group"
            >
              <ChevronLeft className="h-6 w-6 group-hover:text-gray-900" />
            </button>
            
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-400 transition-all duration-300 group"
            >
              <ChevronRight className="h-6 w-6 group-hover:text-gray-900" />
            </button>
            
            {/* Play/Pause Button */}
            <button
              onClick={toggleAutoPlay}
              className="absolute top-6 right-20 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-400 transition-all duration-300 group"
            >
              {isAutoPlaying ? (
                <div className="flex space-x-1">
                  <div className="w-1 h-4 bg-current"></div>
                  <div className="w-1 h-4 bg-current"></div>
                </div>
              ) : (
                <div className="w-0 h-0 border-l-[6px] border-l-current border-y-[4px] border-y-transparent ml-1"></div>
              )}
            </button>
            
            {/* Zoom Button */}
            <button
              onClick={() => openFullscreen(currentImageIndex)}
              className="absolute top-6 right-6 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-400 transition-all duration-300 group"
            >
              <ZoomIn className="h-6 w-6 group-hover:text-gray-900" />
            </button>
          </div>
          
          {/* Thumbnail Strip - Optimized */}
          <div className="flex justify-center mt-12 space-x-2 overflow-x-auto pb-4 scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`relative flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-colors duration-150 ${
                  index === currentImageIndex
                    ? 'border-amber-400 shadow-lg shadow-amber-400/40'
                    : 'border-gray-600 hover:border-amber-400/60'
                }`}
              >
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  style={{ pointerEvents: 'none' }}
                />
                {index === currentImageIndex && (
                  <div className="absolute inset-0 bg-amber-400/20"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Fullscreen Image Viewer */}
        {isFullscreenOpen && (
          <div className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
              {/* Close Button */}
              <button
                onClick={() => setIsFullscreenOpen(false)}
                className="absolute top-6 right-6 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-red-500 hover:border-red-400 transition-all duration-300 z-10"
              >
                <X className="h-6 w-6" />
              </button>
              
              {/* Image */}
              <img
                src={galleryImages[fullscreenImageIndex].src}
                alt={galleryImages[fullscreenImageIndex].title}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={prevFullscreenImage}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-16 h-16 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-400 transition-all duration-300"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              
              <button
                onClick={nextFullscreenImage}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-16 h-16 bg-gray-900/80 backdrop-blur-sm border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-amber-500 hover:border-amber-400 transition-all duration-300"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
              
              {/* Image Info */}
              <div className="absolute bottom-6 left-6 right-6 text-center">
                <h3 className="text-3xl font-serif text-white mb-2">
                  {galleryImages[fullscreenImageIndex].title}
                </h3>
                <p className="text-amber-400 font-light tracking-wide uppercase text-lg">
                  {galleryImages[fullscreenImageIndex].category}
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  {fullscreenImageIndex + 1} of {galleryImages.length}
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      
      {/* Architectural Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden" style={{ top: '18rem' }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
            style={{ 
              filter: 'brightness(0.7) contrast(1.1)',
              opacity: 1
            }}
          >
            <source src="https://snt8981skak946ql.public.blob.vercel-storage.com/Video1.mp4" type="video/mp4" />
            {/* Fallback background if video fails to load */}
            <div className="absolute inset-0 bg-gray-900"></div>
          </video>
          
          {/* Light overlay for better text readability */}
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        <div className="max-w-8xl mx-auto px-6 lg:px-8 relative pt-72">
          <AnimatedSection delay={200}>
            <div className="text-center mb-16">
              <div className="inline-block mb-8">
                
              </div>
              <h1 className="text-5xl md:text-7xl font-light mb-8 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="font-normal bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">Portfolio</span>
                <br />of Excellence
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Explore our exceptional wood and steel structural projects that showcase master-level craftsmanship
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Investment Metrics Section */}
      <section className="relative bg-gray-800 py-24">
        {/* Architectural Accent Line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-500"></div>
        
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection delay={200}>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
                Proven Excellence
              </h2>
              <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto">
                Numbers that reflect our commitment to architectural precision
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {portfolioStats.map((stat, idx) => (
              <AnimatedSection key={idx} delay={400 + idx * 100}>
                <div className="text-center group">
                  <div className="w-20 h-20 bg-gray-700 border border-amber-400/30 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-amber-500/10 transition-all duration-500">
                    <stat.icon className="h-8 w-8 text-amber-400" />
                  </div>
                  <div className="text-4xl md:text-5xl font-serif text-white mb-2">
                    {stat.prefix}
                    <AnimatedCounter end={stat.value} duration={2500} />
                    {stat.suffix}
                  </div>
                  <div className="text-gray-400 font-light tracking-wide uppercase text-sm">
                    {stat.label}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Carousel Section */}
      <section className="relative bg-gray-800 py-24">
        {/* Architectural Accent Line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-500"></div>
        
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection delay={200}>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
                Project <span className="text-amber-400">Gallery</span>
              </h2>
              <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto">
                Explore our finest work through this curated collection of framing excellence
              </p>
            </div>
          </AnimatedSection>
          
          <GalleryCarousel />
        </div>
      </section>

      {/* Category Filter Section */}
      <section className="relative bg-gray-900 py-24">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection delay={200}>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
                Browse by <span className="text-amber-400">Expertise</span>
              </h2>
              <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto">
                Filter our projects by specialization to find examples that match your vision
              </p>
            </div>
          </AnimatedSection>
          
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {categories.map((category, idx) => (
              <AnimatedSection key={category.id} delay={300 + idx * 100}>
                <button
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-3 px-8 py-4 rounded-lg font-sans font-medium transition-all duration-500 transform hover:scale-105 border ${
                    selectedCategory === category.id
                      ? 'bg-amber-500 text-gray-900 border-amber-500 shadow-lg shadow-amber-500/25'
                      : 'bg-gray-800 text-gray-300 border-gray-600 hover:border-amber-400 hover:text-amber-400 hover:bg-gray-700'
                  }`}
                >
                  <category.icon className="h-5 w-5" />
                  <span className="tracking-wide">{category.name}</span>
                </button>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Project Section */}
      <section className="relative bg-gray-900 py-24">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection delay={200}>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
                <span className="text-amber-400">Featured</span> Project
              </h2>
              <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto">
                Our most ambitious dual mastery project showcasing wood and steel structural excellence
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <div className="group relative border border-gray-700 rounded-3xl overflow-hidden transform hover:scale-[1.02] transition-all duration-700 hover:shadow-2xl hover:shadow-amber-500/20 hover:border-amber-400/50 shadow-xl shadow-gray-900/50">
              
              {/* Full Card Background Video */}
              <div className="absolute inset-0 w-full h-full">
                <video
                  id="featured-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  style={{ 
                    filter: 'brightness(0.3) contrast(1.1)', 
                    opacity: 1 
                  }}
                  onLoadedData={(e) => e.target.play()}
                  onError={(e) => console.log('Video error:', e)}
                  onEnded={(e) => e.target.currentTime = 0}
                >
                  <source src="https://snt8981skak946ql.public.blob.vercel-storage.com/Peoria_MalapaiBuilders.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Hidden Image Element for Gallery Clicks */}
                <img
                  id="featured-main-image"
                  src="/Peoria_MalapaiBuilders1.jpg"
                  alt="Peoria Malapai Builders Project"
                  className="w-full h-full object-cover hidden"
                  style={{ 
                    filter: 'brightness(0.3) contrast(1.1)', 
                    opacity: 1 
                  }}
                />
                
                <div className="absolute inset-0 bg-gray-900/60"></div>
              </div>
              
              {/* Featured Project Content */}
              <div className="relative z-10 p-12">
                {/* Project Location & Year */}
                <div className="flex items-center justify-between mb-8 text-white">
                  <div className="flex items-center space-x-3 text-lg">
                    <MapPin className="h-6 w-6 text-amber-400" />
                    <span className="font-light text-xl">Peoria, AZ</span>
                  </div>
                  <span className="text-amber-400 text-2xl font-bold">2022</span>
                </div>
                
                <div className="flex items-start justify-between mb-8">
                  <h3 className="text-4xl font-serif text-white group-hover:text-amber-400 transition-colors duration-300">
                    Peoria Malapai Builders Project
                  </h3>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-6 w-6 text-amber-400 fill-current" />
                    ))}
                  </div>
                </div>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed font-light max-w-4xl">
                  Our most comprehensive dual mastery project combining premium wood framing with advanced steel structural elements. 
                  This luxury custom home showcases our expertise in complex structural engineering and precision craftsmanship.
                </p>
                
                {/* Project Stats */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-serif text-amber-400 mb-2">16 Weeks</div>
                    <div className="text-gray-400 font-light">Duration</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-serif text-amber-400 mb-2">Dual Mastery</div>
                    <div className="text-gray-400 font-light">Framing Type</div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {['Complete wood framing', 'Steel structural elements', 'Custom connections', 'Engineering coordination', 'Quality craftsmanship', 'Large spans'].map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {/* Builder Information */}
                <div className="p-6 bg-gray-700/50 border border-amber-400/30 rounded-xl">
                  <div className="flex items-center space-x-3 text-lg">
                    <Building className="h-6 w-6 text-amber-400" />
                    <span className="text-gray-300 font-light">Builder:</span>
                    <span className="text-amber-400 font-bold text-xl">Malapai Builders</span>
                  </div>
                </div>
                
                {/* Project Gallery */}
                <div className="mt-8">
                  <h4 className="font-serif text-white text-xl mb-6">Project Gallery</h4>
                  <div className="grid grid-cols-4 gap-4">
                                            {[
                          '/Peoria_MalapaiBuilders1.jpg',
                          '/Peoria_MalapaiBuilders2.jpg',
                          '/Peoria_MalapaiBuilders3.jpg',
                          '/Peoria_MalapaiBuilders4.jpg',
                          '/Peoria_MalapaiBuilders5.jpg',
                          '/Peoria_MalapaiBuilders6.jpg',
                          '/Peoria_MalapaiBuilders7.jpg',
                          '/Peoria_MalapaiBuilders8.jpg'
                        ].map((img, idx) => (
                          <div 
                            key={idx} 
                            className="relative group cursor-pointer"
                            onClick={(e) => {
                              // Simple approach: just change the main display
                              const videoElement = document.querySelector('#featured-video');
                              const imgElement = document.querySelector('#featured-main-image');
                              
                              if (videoElement && imgElement) {
                                // If video is currently showing, hide it and show image
                                if (videoElement.style.display !== 'none') {
                                  videoElement.style.display = 'none';
                                  imgElement.style.display = 'block';
                                  imgElement.src = img;
                                } else {
                                  // If image is showing, just change to the clicked one
                                  imgElement.src = img;
                                }
                              }
                            }}
                          >
                            <img
                              src={img}
                              alt={`Peoria Malapai Builders Project - View ${idx + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-gray-600 group-hover:border-amber-400 transition-colors duration-300"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                              <Eye className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="relative bg-gray-800 py-24">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 auto-rows-fr">
            {filteredProjects.map((project, idx) => (
              <AnimatedSection key={project.id} delay={300 + idx * 150}>
                <div className="group bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden transform hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20 hover:border-amber-400/50 shadow-lg shadow-gray-900/50 flex flex-col h-full">
                  
                  {/* Project Image */}
                  <div className="relative h-72 overflow-hidden">
                    {project.image && project.image !== '/api/placeholder/600/400' ? (
                      <img 
                        src={project.image} 
                        alt={`${project.title} - ${project.location}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800"></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                  </div>
                  
                  {/* Project Content */}
                  <div className="p-10 flex flex-col flex-grow">
                    {/* Project Location & Year */}
                    <div className="flex items-center justify-between mb-6 text-sm text-gray-400">
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-amber-400" />
                        <span>{project.location}</span>
                      </div>
                      <span className="text-amber-400">{project.completed}</span>
                    </div>
                                        {/* Additional Project Images */}
                    {project.images && project.images.length > 1 && (
                      <div className="mb-6">
                        <h4 className="font-serif text-white text-sm mb-3">Project Gallery:</h4>
                        <div className="grid grid-cols-4 gap-2">
                          {project.images.slice(1, 5).map((img, idx) => (
                            <div 
                              key={idx} 
                              className="relative group cursor-pointer"
                              onClick={() => {
                                // Simple approach: just change the main image
                                project.image = img;
                                // Force re-render
                                setFilteredProjects([...filteredProjects]);
                              }}
                            >
                              <img
                                src={img}
                                alt={`${project.title} - View ${idx + 2}`}
                                className="w-full h-16 object-cover rounded-lg border border-gray-600 group-hover:border-amber-400 transition-colors duration-300"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:border-amber-400 transition-colors duration-300 rounded-lg flex items-center justify-center">
                                <Eye className="h-4 w-4 text-white" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-6">
                      <h3 className="text-2xl font-serif text-white group-hover:text-amber-400 transition-colors duration-300">
                        {project.title}
                      </h3>
                      <div className="flex items-center space-x-1">
                        {[...Array(project.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-amber-400 fill-current" />
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-gray-300 mb-6 leading-relaxed font-light">{project.description}</p>
                    
                    {/* Builder & Architect Information */}
                    {(project.builder || project.architect) && (
                      <div className="mb-6 p-4 bg-gray-800/50 border border-amber-400/20 rounded-lg space-y-3">
                        {project.builder && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Building className="h-4 w-4 text-amber-400" />
                            <span className="text-gray-300 font-light">Builder:</span>
                            <span className="text-amber-400 font-medium">{project.builder}</span>
                          </div>
                        )}
                        {project.architect && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Pencil className="h-4 w-4 text-amber-400" />
                            <span className="text-gray-300 font-light">Architect:</span>
                            <span className="text-amber-400 font-medium">{project.architect}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Project Details */}
                    <div className="flex items-center space-x-3 mb-8 text-sm">
                      <Clock className="h-5 w-5 text-amber-400" />
                      <span className="text-gray-300 font-light">{project.duration}</span>
                    </div>
                    
                    {/* Features */}
                    <div className="mb-8">
                      <h4 className="font-serif text-white text-lg mb-4">Key Features:</h4>
                      <div className="space-y-3">
                        {project.features.slice(0, 3).map((feature, featureIdx) => (
                          <div key={featureIdx} className="flex items-center space-x-3 text-sm">
                            <div className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0"></div>
                            <span className="text-gray-300 font-light">{feature}</span>
                          </div>
                        ))}
                        {project.features.length > 3 && (
                          <div className="text-sm text-amber-400 font-medium">
                            +{project.features.length - 3} more architectural features
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <div className="flex justify-center mt-auto pt-6">
                      <button 
                        onClick={() => {
                          setSelectedProject(project);
                          setIsModalOpen(true);
                        }}
                        className="bg-amber-500 hover:bg-amber-400 text-gray-900 px-8 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                      >
                        <Eye className="h-5 w-5" />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
          
          {filteredProjects.length === 0 && (
            <AnimatedSection delay={400}>
              <div className="text-center py-24">
                <div className="w-24 h-24 bg-gray-800 border border-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                  <Search className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-3xl font-serif text-white mb-6">No Projects Found</h3>
                <p className="text-gray-300 max-w-md mx-auto font-light">
                  No projects match the selected category. Try selecting a different filter or browse all projects.
                </p>
              </div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="relative bg-gray-900 py-32">
        {/* Architectural Accent Lines */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-500"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-500"></div>
        
        {/* Ambient Glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection delay={200}>
            <div className="text-center">
              <h2 className="text-5xl md:text-6xl font-serif text-white mb-8 leading-tight">
                Ready to Start <span className="text-amber-400">Your Project?</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-16 leading-relaxed font-light">
                Let's discuss how we can bring your framing vision to life with the same level of craftsmanship shown in our portfolio
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a 
                  href="tel:602-448-1723"
                  className="group inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-gray-900 font-medium rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-amber-500/25"
                >
                  <Building className="h-6 w-6 mr-3" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Begin Your Build</span>
                </a>
                <a 
                  href="/contact"
                  className="group inline-flex items-center justify-center px-8 py-4 border border-gray-600 text-gray-300 hover:border-amber-400 hover:text-amber-400 font-medium rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  <ArrowRight className="h-6 w-6 mr-3" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule Consultation</span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Project Details Modal */}
      {isModalOpen && selectedProject && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-gray-900 border border-gray-700 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-3xl font-serif text-white">{selectedProject.title}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <X className="h-8 w-8" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Main Project Image */}
              <div className="relative h-80 overflow-hidden rounded-2xl mb-6">
                <img
                  id="modal-main-image"
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                

              </div>

              {/* Project Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300 font-light">Location:</span>
                    <span className="text-white font-medium">{selectedProject.location}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300 font-light">Duration:</span>
                    <span className="text-white font-medium">{selectedProject.duration}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-amber-400" />
                    <span className="text-gray-300 font-light">Completed:</span>
                    <span className="text-white font-medium">{selectedProject.completed}</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {selectedProject.builder && (
                    <div className="flex items-center space-x-3">
                      <Building className="h-5 w-5 text-amber-400" />
                      <span className="text-gray-300 font-light">Builder:</span>
                      <span className="text-white font-medium">{selectedProject.builder}</span>
                    </div>
                  )}
                  {selectedProject.architect && (
                    <div className="flex items-center space-x-3">
                      <Pencil className="h-5 w-5 text-amber-400" />
                      <span className="text-gray-300 font-light">Architect:</span>
                      <span className="text-white font-medium">{selectedProject.architect}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Description */}
              <div className="mb-8">
                <h3 className="text-xl font-serif text-white mb-4">Project Overview</h3>
                <p className="text-gray-300 leading-relaxed font-light">{selectedProject.description}</p>
              </div>

              {/* Key Features */}
              <div className="mb-8">
                <h3 className="text-xl font-serif text-white mb-4">Key Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedProject.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3 text-sm">
                      <div className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-300 font-light">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Full Image Gallery */}
              {selectedProject.images && selectedProject.images.length > 1 && (
                <div>
                  <h3 className="text-xl font-serif text-white mb-4">Project Gallery</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedProject.images.map((img, idx) => (
                      <div 
                        key={idx} 
                        className="relative group cursor-pointer"
                        onClick={() => {
                          // Simple approach: just change the main image
                          const modalMainImage = document.querySelector('#modal-main-image');
                          if (modalMainImage) {
                            modalMainImage.src = img;
                          }
                        }}
                      >
                        <img
                          src={img}
                          alt={`${selectedProject.title} - View ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-600 group-hover:border-amber-400 transition-colors duration-300"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                          <Eye className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-3 border border-gray-600 text-gray-300 hover:border-amber-400 hover:text-amber-400 rounded-lg font-medium transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;